/*
 * Die Suche, die auf Beschwerden antwortet.
 *
 * Zwei Elemente teilen sich diese Datei:
 *
 *   <ai-search-overlay>  das Vollbild-Overlay aus dem Header
 *   <ai-search-page>     derselbe Antwortteil auf der Suchergebnisseite
 *
 * Der Ablauf ist an beiden Orten derselbe:
 *
 *   1. Waehrend des Tippens fragt Shopifys eigene Vorschlagssuche
 *      (/search/suggest.json). Die ist in Millisekunden da und kostet nichts -
 *      vor einem Suchfeld ist das der Unterschied zwischen Vorschlag und
 *      Ladebalken.
 *   2. Erst mit Enter geht die Frage an den Worker. Der versteht Saetze
 *      ("seit wochen muede") statt nur Woerter und ist deshalb das, was hier
 *      ueberhaupt gebraucht wird - aber er kostet pro Anfrage ein Modell.
 *   3. Vor Schritt 2 laeuft die Warnliste. Beschwerden, die abgeklaert
 *      gehoeren, beantwortet kein Selbsttest. Die Liste steht im Theme, nicht
 *      im Modell: sie muss verlaesslich greifen, auch wenn der Worker gerade
 *      nicht erreichbar ist.
 */

(() => {
  /* Wie lange nach dem letzten Tastendruck gewartet wird, bevor Shopify nach
     Vorschlaegen gefragt wird. Kurz genug, dass es mitlaeuft. */
  const SUGGEST_DEBOUNCE_MS = 220;

  /* Ab hier lohnt das Fragen ueberhaupt. Zwei Buchstaben liefern die halbe
     Sortimentsliste. */
  const MIN_QUERY_LENGTH = 3;

  /*
   * Wie lange Ruhe sein muss, bis die Suche von selbst losgeht. Enter ist der
   * schnellere Weg, aber niemand soll ihn suchen muessen: wer fertig getippt
   * hat, wartet ohnehin auf die Antwort.
   *
   * Lang genug, dass eine Tipppause mitten im Satz keinen Modellaufruf kostet,
   * kurz genug, dass es sich nicht wie Warten anfuehlt. Seit die Karten aus dem
   * Strom kommen und nicht mehr erst mit dem fertigen Satz, darf hier weniger
   * stehen: die Wartezeit dahinter ist kuerzer geworden.
   */
  const SEARCH_DEBOUNCE_MS = 600;

  /* Und lang genug muss die Frage sein: "ei" ist niemand, der fertig ist. */
  const MIN_AUTO_SEARCH_LENGTH = 6;

  /*
   * Wie lange die Antwort Zeit bekommt, bevor Umrisse erscheinen.
   *
   * Eine Frage, die schon einmal gestellt wurde, ist in rund hundert
   * Millisekunden da. Umrisse, die dafuer kurz aufblitzen und sofort wieder
   * verschwinden, lesen sich langsamer als gar nichts - sie melden ein Warten,
   * das nicht stattfindet. Also erst zeigen, wenn es wirklich dauert.
   */
  const LOADING_DELAY_MS = 200;

  /* Wie viele Treffer der Worker schreiben soll. Seine Antwortzeit haengt fast
     nur an dieser Zahl, und mehr als sechs Tests sieht sich niemand an - das
     Sortiment hat elf. */
  const RESULT_LIMIT = 6;

  const ICON = {
    search:
      '<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7.25" stroke="currentColor" stroke-width="1.6"/><path d="M20.5 20.5 16.4 16.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    check:
      '<svg class="icon" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M3 8.4 6.3 11.7 13 5" stroke="#45624e" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    bulb: '<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 3.5a5.5 5.5 0 0 0-3.2 9.97c.55.4.87 1.02.87 1.68v.35h4.66v-.35c0-.66.32-1.29.87-1.68A5.5 5.5 0 0 0 12 3.5Z" stroke="#45624e" stroke-width="1.5" stroke-linejoin="round"/><path d="M10 18.5h4M10.7 21h2.6" stroke="#45624e" stroke-width="1.5" stroke-linecap="round"/></svg>',
    warning:
      '<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 4.5 21 19.5H3L12 4.5Z" stroke="#a04a3c" stroke-width="1.6" stroke-linejoin="round"/><path d="M12 10v4" stroke="#a04a3c" stroke-width="1.7" stroke-linecap="round"/><circle cx="12" cy="16.6" r="1" fill="#a04a3c"/></svg>',
    info: '<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.75" stroke="#6f6f6f" stroke-width="1.5"/><path d="M12 11v5.5" stroke="#6f6f6f" stroke-width="1.6" stroke-linecap="round"/><circle cx="12" cy="7.9" r="1" fill="#6f6f6f"/></svg>',
    close:
      '<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  };

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Der Worker liefert die Antwort-Tags klein ("vorsorge"), angezeigt werden sie gross. */
  function capitalize(value) {
    const text = String(value ?? '');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Preise kommen vom Worker als Betrag und Waehrungscode, nicht als fertiger
   * Satz - die Storefront weiss selbst am besten, wie sie ihn schreibt.
   */
  function formatMoney(price) {
    if (!price || price.amount == null) return '';
    const amount = Number(price.amount);
    if (Number.isNaN(amount)) return '';

    const locale = (window.Shopify && window.Shopify.locale) || 'de';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: price.currencyCode || 'EUR',
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(2)} ${price.currencyCode || ''}`.trim();
    }
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Die Konfiguration steht einmal auf der Seite, als JSON-Block aus
   * snippets/ai-search-config.liquid. Ein kaputter Eintrag im Customizer darf
   * die Suche nicht mitnehmen - dann gilt eben die leere Vorgabe.
   */
  let configCache = null;

  function config() {
    if (configCache) return configCache;

    const node = document.getElementById('ai-search-config');
    if (!node) {
      configCache = {};
      return configCache;
    }

    try {
      configCache = JSON.parse(node.textContent) || {};
    } catch (error) {
      console.warn('ai-search: Konfiguration ist kein gueltiges JSON', error);
      configCache = {};
    }
    return configCache;
  }

  /**
   * Die Warnliste. Jeder Eintrag ist eine Wortfolge, die in der Frage
   * vorkommen muss - "blut im urin" trifft auch "seit gestern blut im urin",
   * aber nicht "blutdruck".
   *
   * Absichtlich stumpf gehalten: eine Liste, die jeder im Team lesen und
   * ergaenzen kann, schlaegt hier eine Klugheit, die niemand pruefen kann.
   */
  function matchesFlag(query, flags) {
    const haystack = ` ${query.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ')} `;
    return flags.find((flag) => {
      const needle = String(flag.match || '')
        .toLowerCase()
        .trim();
      if (!needle) return false;
      return haystack.includes(` ${needle} `) || haystack.includes(`${needle} `);
    });
  }

  /**
   * Rueckfragen: Begriffe, die fuer sich genommen zu weit sind. "kinderwunsch"
   * trifft vier Tests fuer drei verschiedene Leute - da ist eine Frage
   * ehrlicher als eine Liste.
   */
  function matchesClarifier(query, clarifiers) {
    const normalized = query.toLowerCase().trim();
    return clarifiers.find((entry) =>
      (entry.match || '')
        .split(',')
        .map((word) => word.trim().toLowerCase())
        .filter(Boolean)
        .some((word) => normalized === word || normalized === `${word}?`)
    );
  }

  /* ------------------------------------------------------------------ *
   * Die Landkarte: was das Theme selbst beantworten kann.
   * ------------------------------------------------------------------ */

  /*
   * Umlaute umgeschrieben, damit "muede" und "müde" dasselbe Wort sind. Die
   * gleiche Regel gilt im Worker - wer sie hier aendert, muss sie dort
   * mitaendern, sonst raten Theme und Modell verschieden.
   */
  function fold(text) {
    return String(text ?? '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss');
  }

  /*
   * Woerter, die in jeder zweiten Frage und in jedem zweiten Produkttitel
   * stehen. Ohne sie traf "geschenk fuer meine mutter" das "Intim-Check Set
   * fuer die Frau" - wegen "fuer".
   */
  const NOISE = new Set([
    'der', 'die', 'das', 'dem', 'den', 'des', 'ein', 'eine', 'einen', 'einem', 'einer',
    'und', 'oder', 'aber', 'mit', 'ohne', 'fuer', 'von', 'vom', 'zum', 'zur', 'auf',
    'aus', 'bei', 'nach', 'seit', 'ueber', 'unter', 'nicht', 'auch', 'noch', 'schon',
    'ich', 'mir', 'mich', 'mein', 'meine', 'meinen', 'meinem', 'meiner', 'sich',
    'hab', 'habe', 'haben', 'hat', 'bin', 'ist', 'sind', 'war', 'wird', 'werden',
    'wie', 'was', 'wann', 'warum', 'welche', 'welcher', 'welches', 'man', 'immer',
    'the', 'and', 'for', 'with', 'from', 'this', 'that', 'have', 'has', 'are',
    'was', 'were', 'you', 'your', 'what', 'when', 'how', 'why', 'which',
  ]);

  function words(text) {
    return fold(text)
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length > 2 && !NOISE.has(word));
  }

  /*
   * Ab hier gilt ein gemeinsamer Wortanfang als dasselbe Wort. Fuenf Zeichen
   * sind kurz genug fuer "magen" in "magenprobleme" und lang genug, dass nicht
   * jedes Produkt bei jeder Frage mitkommt.
   */
  const STEM_MIN = 5;

  /* Fuer den Fall, dass ein Wort ganz am Anfang des anderen steht, reicht ein
     Zeichen weniger - dort ist die Uebereinstimmung ja vollstaendig. */
  const STEM_SHORT = 4;

  /* Die Endung weg, aber nur eine: kein Stemmer, nur so viel, dass "haare" als
     "haar" in "haarausfall" wiederzufinden ist. */
  function stem(word) {
    return word.length < 5 ? word : word.replace(/(en|er|es|e|n|s)$/, '');
  }

  /* Wie weit zwei Woerter denselben Anfang haben. */
  function sharedPrefix(a, b) {
    const end = Math.min(a.length, b.length);
    let i = 0;
    while (i < end && a[i] === b[i]) i++;
    return i;
  }

  /**
   * Ob ein Wort in der Frage vorkommt - auch als Teil eines zusammengesetzten.
   *
   * Deutsch schreibt zusammen, was zusammengehoert: wer "magenprobleme" tippt,
   * meint den Magen, und "haare" und "haarausfall" sind dieselbe Beschwerde.
   * Auf Gleichheit zu pruefen hiesse, das meiste davon zu verpassen - Leute
   * tippen das Wort, das ihnen einfaellt, nicht das aus der Liste.
   */
  function matchesWord(word, asked) {
    if (asked.has(word)) return true;
    if (word.length < STEM_MIN) return false;

    const left = stem(word);

    for (const other of asked) {
      if (other.length < STEM_MIN) continue;

      // Zusammengesetzt gegen zusammengesetzt: "magenprobleme" und
      // "magenschmerzen" teilen den Anfang, keines steckt im anderen.
      if (sharedPrefix(word, other) >= STEM_MIN) return true;

      // Und das Grundwort gegen das Zusammengesetzte: "haare" steckt als
      // "haar" in "haarausfall", teilt mit ihm aber nur vier Zeichen.
      const right = stem(other);
      const shorter = left.length <= right.length ? left : right;
      const longer = left.length <= right.length ? right : left;
      if (shorter.length >= STEM_SHORT && longer.startsWith(shorter)) return true;
    }
    return false;
  }

  /* Wie viele Karten das Theme hoechstens von sich aus zeigt. Drei sind genug,
     um die Wartezeit zu fuellen, und wenig genug, dass das Umsortieren durch
     den Worker nicht wie ein Sprung wirkt. */
  const GUESS_LIMIT = 3;

  /* Darunter ist es kein Treffer, sondern ein Zufall: ein einzelnes kurzes
     Wort, das in zwei Beschreibungen vorkommt, soll keine Karte rechtfertigen. */
  const GUESS_MIN_SCORE = 3;

  /**
   * Die Vermutung des Themes, welche Tests zu dieser Beschwerde passen.
   *
   * Kein Modell, nur die Suchwoerter, die der Shop ohnehin pflegt. Eine ganze
   * Phrase wiegt schwer ("brennen beim wasserlassen" ist eindeutig), ein
   * einzelnes Wort wenig ("test" steht ueberall). Was hier herauskommt, ist
   * ungefaehr - und es steht in null Millisekunden da, statt in anderthalb
   * Sekunden.
   */
  function guessProducts(query, catalog) {
    if (!Array.isArray(catalog) || !catalog.length) return [];

    const haystack = ` ${fold(query).replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `;
    const asked = new Set(words(query));
    if (!asked.size) return [];

    return catalog
      .map((product) => {
        let score = 0;

        for (const term of product.terms || []) {
          const phrase = fold(term).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
          if (!phrase) continue;

          // Die ganze Wortfolge in der Frage: das ist der sichere Treffer, und
          // je laenger sie ist, desto weniger kann sie Zufall sein.
          if (haystack.includes(` ${phrase} `)) {
            score += 3 + phrase.split(' ').length;
            continue;
          }

          /*
           * Ein langes Wort ist ein eigener Beweis: "haarausfall" steht in
           * keiner Frage zufaellig, "magen" schon eher. Deshalb zaehlt die
           * Laenge mit, sonst faellt ein Volltreffer wie "haarausfall" unter
           * die Schwelle, nur weil er aus einem Wort besteht.
           */
          for (const word of phrase.split(' ')) {
            if (word.length > 3 && matchesWord(word, asked)) score += word.length >= 8 ? 3 : 1;
          }
        }

        for (const word of words(product.title)) {
          if (matchesWord(word, asked)) score += 2;
        }

        return { product, score };
      })
      .filter((entry) => entry.score >= GUESS_MIN_SCORE)
      .sort((a, b) => b.score - a.score)
      .slice(0, GUESS_LIMIT)
      .map((entry) => entry.product);
  }

  /* ------------------------------------------------------------------ *
   * Der gemeinsame Teil: fragen und rendern.
   * ------------------------------------------------------------------ */

  class SearchEngine {
    constructor(host) {
      this.host = host;
      this.controller = null;
    }

    get settings() {
      return config();
    }

    /** Bricht eine laufende Anfrage ab - die Antwort darauf will niemand mehr. */
    abort() {
      this.controller?.abort();
      this.controller = null;
      this.asking = false;
    }

    /*
     * Dasselbe, aber nur fuer den Worker.
     *
     * Wer weitertippt, hat die vorige Frage zurueckgezogen. Die lief bisher zu
     * Ende und wurde erst vom naechsten run() abgeraeumt: ein offener Stream,
     * der mitgelesen wird, und eine Verbindung, die der naechsten Frage fehlt.
     * Die Vorschlagssuche bleibt davon unberuehrt - die soll ja mitlaufen.
     */
    abortAsk() {
      if (this.asking) this.abort();
    }

    /**
     * Shopifys eigene Vorschlaege. Sie kennen den Katalog, aber keine Saetze:
     * dafuer sind sie sofort da.
     */
    async suggest(query) {
      this.abort();
      this.controller = new AbortController();
      this.asking = false;

      const url = new URL(`${rootUrl()}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,query');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`suggest responded ${response.status}`);

      const data = await response.json();
      return data?.resources?.results ?? { queries: [], products: [] };
    }

    /**
     * Die eigentliche Frage an den Worker.
     *
     * 404 ist dort eine Antwort, kein Fehler: "kenne ich nichts dazu" kommt mit
     * leerer Handle-Liste und ist genau das, was der Nutzer wissen will.
     */
    async ask(query, onPartial) {
      /*
       * 404 ist beim Worker zweierlei: "zu dieser Frage nichts gefunden" kommt
       * mit einer leeren handles-Liste, "diesen Shop kenne ich nicht" ohne.
       * Das zweite ist ein Konfigurationsfehler und darf dem Kunden nie als
       * "fuehren wir nicht" erklaert werden - dann stimmt die Aussage naemlich
       * fuer das ganze Sortiment.
       */
      const check = (data) => {
        if (!Array.isArray(data?.handles)) {
          throw new Error(data?.error || 'search worker returned no handles');
        }
        return data;
      };

      this.abort();
      this.controller = new AbortController();
      this.asking = true;

      const { endpoint, shop, language } = this.settings;
      if (!endpoint || !shop) throw new Error('ai-search: endpoint oder shop fehlt');

      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('query', query);
      url.searchParams.set('shop', shop);
      url.searchParams.set('limit', String(RESULT_LIMIT));
      /*
       * Ohne answer=1 antwortet der Worker nur mit Handles. Erst damit schreibt
       * das Modell den Satz ueber den Karten, die Begruendung je Treffer
       * ("passt: ...") und die Vorschlaege, was man als Naechstes suchen
       * koennte. Es kostet Antwortzeit, weil das Modell mehr schreibt - und es
       * ist genau das, was diese Suche von einer Trefferliste unterscheidet.
       */
      url.searchParams.set('answer', '1');
      /*
       * Die Handles stehen im JSON des Modells vor dem Satz. Wer einen Rueckruf
       * mitgibt, bekommt sie deshalb zweimal: die Karten, sobald sie feststehen,
       * und dieselbe Antwort noch einmal mit Ueberschrift und Begruendungen.
       */
      if (typeof onPartial === 'function') url.searchParams.set('stream', '1');
      if (language) url.searchParams.set('language', language);

      // Als String, weil mehrere Apps in window.fetch haengen und das erste
      // Argument als Zeichenkette behandeln - siehe predictive-search.js.
      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok && response.status !== 404) {
        throw new Error(`search worker responded ${response.status}`);
      }

      const streamed = (response.headers.get('Content-Type') || '').includes('ndjson');
      if (!streamed || !response.body) {
        return check(await response.json());
      }

      /*
       * Eine Zeile je Zustand. Ein Netz schneidet mitten im Text, also wird
       * gesammelt, bis ein Zeilenumbruch kommt - alles danach ist der Anfang der
       * naechsten Zeile und bleibt liegen.
       */
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let last = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let cut = buffer.indexOf('\n');
        while (cut !== -1) {
          const line = buffer.slice(0, cut).trim();
          buffer = buffer.slice(cut + 1);
          cut = buffer.indexOf('\n');
          if (!line) continue;

          let event;
          try {
            event = JSON.parse(line);
          } catch {
            continue;
          }

          if (event.type === 'error') {
            throw new Error(event.payload?.error || 'search worker failed');
          }
          if (event.type === 'partial') {
            if (Array.isArray(event.payload?.handles)) onPartial(event.payload);
            continue;
          }
          last = event.payload;
        }
      }

      if (!last) throw new Error('search worker returned no answer');
      return check(last);
    }
  }

  /* ------------------------------------------------------------------ *
   * Bausteine, die beide Elemente rendern.
   * ------------------------------------------------------------------ */

  /*
   * Der Antwortblock, solange er noch geschrieben wird. Er steht an derselben
   * Stelle und hat dieselbe Hoehe wie der fertige, damit die Karten darunter
   * nicht springen, wenn der Satz eintrifft.
   */
  function answerBones(label) {
    return `
      <div class="ai-search__answer ai-search__answer--loading" role="status" aria-label="${escapeHtml(
        label
      )}">
        <span class="ai-search__bone ai-search__bone--label"></span>
        <span class="ai-search__bone ai-search__bone--headline"></span>
        <span class="ai-search__bone ai-search__bone--line"></span>
        <span class="ai-search__bone ai-search__bone--line ai-search__bone--short"></span>
      </div>`;
  }

  function answerBlock({ label, heading, text, chips }) {
    return `
      <div class="ai-search__answer">
        <div class="ai-search__answer-label">${ICON.bulb}<span>${escapeHtml(label)}</span></div>
        <h2 class="ai-search__answer-heading">${escapeHtml(heading)}</h2>
        ${text ? `<p class="ai-search__answer-text">${escapeHtml(text)}</p>` : ''}
        ${
          chips && chips.length
            ? `<div class="ai-search__chips">${chips
                .map((chip) => `<span class="ai-search__chip">${escapeHtml(capitalize(chip))}</span>`)
                .join('')}</div>`
            : ''
        }
      </div>
    `;
  }

  function flagBlock(flag, texts) {
    return `
      <div class="ai-search__flag" role="alert">
        <div class="ai-search__flag-label">${ICON.warning}<span>${escapeHtml(
          texts.flagLabel
        )}</span></div>
        <h2 class="ai-search__flag-heading">${escapeHtml(flag.heading || texts.flagHeading)}</h2>
        <p class="ai-search__flag-text">${escapeHtml(flag.text || texts.flagText)}</p>
      </div>
    `;
  }

  /**
   * Eine Ergebniskarte.
   *
   * `reasons` sind die Gruppen, ueber die der Worker den Treffer begruendet -
   * die Zeile, die diese Suche von einer Trefferliste unterscheidet. Fehlt sie,
   * faellt die Karte auf die Produktbeschreibung zurueck, nie auf eine
   * erfundene Begruendung.
   */
  function resultCard(product, texts) {
    const price = formatMoney(product.price);
    const reason = (product.reasons || []).filter(Boolean).join(' · ');
    const url = `${rootUrl()}products/${encodeURIComponent(product.handle)}`;

    return `
      <li class="ai-search__card" data-handle="${escapeHtml(product.handle)}">
        <div class="ai-search__card-media">
          ${
            product.image
              ? `<img class="ai-search__card-image" src="${escapeHtml(
                  product.image
                )}" alt="" loading="lazy" width="104" height="104">`
              : '<span class="ai-search__card-image"></span>'
          }
          <div class="ai-search__card-info">
            <a class="ai-search__card-title" href="${escapeHtml(url)}">${escapeHtml(
              product.title
            )}</a>
            ${
              reason
                ? `<span class="ai-search__card-reason">${ICON.check}<span>${escapeHtml(
                    reason
                  )}</span></span>`
                : ''
            }
            ${
              product.note
                ? `<span class="ai-search__card-note">${escapeHtml(product.note)}</span>`
                : ''
            }
          </div>
        </div>
        <div class="ai-search__card-buy">
          ${
            price
              ? `<span class="ai-search__card-price"><span class="ai-search__card-amount">${escapeHtml(
                  price
                )}</span></span>`
              : ''
          }
          <a class="ai-search__card-button" href="${escapeHtml(url)}" data-ai-add>${escapeHtml(
            texts.cardButton
          )}</a>
        </div>
      </li>
    `;
  }

  /**
   * Eine Zeile in der Vorschlagsliste waehrend des Tippens.
   *
   * Bewusst schmaler als die Ergebniskarte und ohne Warenkorb-Knopf: hier ist
   * noch nichts beantwortet. Wer waehrend des Tippens schon kaufen soll, liest
   * die Antwort nicht mehr, die eine Zeile tiefer entstehen wuerde.
   */
  function suggestionRow(product) {
    const price = formatMoney(product.price);
    const url = `${rootUrl()}products/${encodeURIComponent(product.handle)}`;

    return `
      <a class="ai-search__suggestion ai-search__suggestion--product" href="${escapeHtml(url)}">
        ${
          product.image
            ? `<img class="ai-search__suggestion-image" src="${escapeHtml(
                product.image
              )}" alt="" loading="lazy" width="52" height="52">`
            : '<span class="ai-search__suggestion-image"></span>'
        }
        <span class="ai-search__suggestion-text">
          <span class="ai-search__suggestion-title">${escapeHtml(product.title)}</span>
        </span>
        ${price ? `<span class="ai-search__suggestion-price">${escapeHtml(price)}</span>` : ''}
      </a>
    `;
  }

  function disclaimerBlock(text) {
    if (!text) return '';
    return `
      <div class="ai-search__disclaimer">${ICON.info}<p>${escapeHtml(text)}</p></div>
    `;
  }

  /* ------------------------------------------------------------------ *
   * In den Warenkorb, direkt aus der Karte.
   * ------------------------------------------------------------------ */

  /**
   * Der Worker kennt nur Handles, der Warenkorb braucht eine Varianten-ID.
   * Die steht in /products/<handle>.js, einer Datei, die das CDN ausliefert.
   *
   * Produkte mit mehreren Varianten bekommen keinen Warenkorb-Knopf: welche
   * gemeint ist, entscheidet die Produktseite, nicht die Suche. Bei den
   * Selbsttests ist das der Normalfall von genau einer Variante.
   */
  async function resolveVariant(handle) {
    const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return null;

    const product = await response.json();
    if (!Array.isArray(product.variants) || product.variants.length !== 1) return null;
    return product.variants[0].available ? product.variants[0].id : null;
  }

  async function addToCart(variantId) {
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];

    const response = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const state = await response.json();
    if (state.status) throw new Error(state.description || state.message || 'cart/add failed');

    // Dasselbe Muster wie ueberall im Theme: der Drawer rendert sich aus den
    // mitgelieferten Sections neu und geht auf.
    if (drawer && state.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(state);
    }
    return state;
  }

  /* ------------------------------------------------------------------ *
   * Das Overlay.
   * ------------------------------------------------------------------ */

  class AiSearchOverlay extends HTMLElement {
    connectedCallback() {
      this.engine = new SearchEngine(this);
      this.input = this.querySelector('[data-ai-input]');
      this.results = this.querySelector('[data-ai-results]');
      this.aside = this.querySelector('[data-ai-aside]');
      this.field = this.querySelector('[data-ai-field]');
      this.idleMarkup = this.results ? this.results.innerHTML : '';

      this.texts = config().texts || {};
      this.flags = config().flags || [];
      this.clarifiers = config().clarifiers || [];
      this.catalog = config().catalog || [];

      this.suggestTimer = null;
      this.searchTimer = null;
      this.loadingTimer = null;
      this.lastQuery = '';

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.input?.addEventListener('input', this.onInput);

      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      clearTimeout(this.suggestTimer);
      clearTimeout(this.searchTimer);
      clearTimeout(this.loadingTimer);
      this.engine?.abort();
    }

    /* Jeder Knopf mit data-ai-search-open macht die Suche auf, egal wo er im
       Theme sitzt - Header, Startseite, Fehlerseite. */
    onDocumentClick = (event) => {
      const trigger = event.target.closest?.('[data-ai-search-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger.dataset.aiSearchQuery || '');
    };

    onClick = (event) => {
      const close = event.target.closest('[data-ai-close]');
      if (close) {
        event.preventDefault();
        return this.close();
      }

      const clear = event.target.closest('[data-ai-clear]');
      if (clear) {
        event.preventDefault();
        this.input.value = '';
        this.classList.remove('ai-search--filled');
        this.showIdle();
        this.input.focus();
        return;
      }

      // Beschwerde-Einstieg, Vorschlagszeile und Rueckfrage-Antwort tragen
      // alle die fertige Frage bei sich.
      const query = event.target.closest('[data-ai-query]');
      if (query) {
        event.preventDefault();
        this.input.value = query.dataset.aiQuery;
        this.classList.add('ai-search--filled');
        return this.run(query.dataset.aiQuery);
      }

      const add = event.target.closest('[data-ai-add]');
      if (add) return this.addFromCard(event, add);
    };

    onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Enter' && event.target === this.input) {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        const value = this.input.value.trim();
        /* Dieselbe Frage, die schon beantwortet ist oder unterwegs, wird nicht
           noch einmal gestellt: run() faengt bei null an, die Ergebnisse
           verschwanden und kamen identisch wieder - auf dem Telefon blitzte
           dabei die ganze Seite. */
        const alreadyAsked = value === this.lastQuery;
        if (value.length >= MIN_QUERY_LENGTH && !alreadyAsked) this.run(value);

        /* Auf dem Telefon heisst die Such-Taste der Tastatur "fertig". Die
           Antwort laeuft dann meist schon von selbst, ohne das hier sah die
           Taste tot aus und die Tastatur blieb ueber den Ergebnissen. Am
           Desktop bleibt der Cursor, dort tippt man vielleicht weiter. */
        if (window.matchMedia('(hover: none)').matches) this.input.blur();
      }
    };

    onInput = () => {
      const value = this.input.value.trim();
      this.classList.toggle('ai-search--filled', value.length > 0);

      clearTimeout(this.suggestTimer);
      clearTimeout(this.searchTimer);

      /*
       * Wer weitertippt, wartet nicht mehr auf die Antwort von vorhin. Der
       * Worker rechnet sie ohnehin zu Ende und legt sie in seinen Cache - hier
       * geht es nur darum, die Leitung und den Stream freizugeben, bevor die
       * naechste Frage sie braucht.
       */
      if (value !== this.lastQuery) this.engine.abortAsk();

      if (value.length < MIN_QUERY_LENGTH) {
        this.engine.abort();
        return this.showIdle();
      }

      /*
       * Die Warnung wartet nicht auf Enter. Wer "blut im urin" tippt, bekam
       * sonst erst eine Liste mit Warenkorb-Knoepfen zu sehen und die Warnung
       * einen Tastendruck spaeter - in der Reihenfolge liest sie niemand mehr.
       */
      const flag = matchesFlag(value, this.flags);
      if (flag) {
        clearTimeout(this.searchTimer);
        this.clearLoading();
        this.engine.abort();
        this.lastQuery = value;
        this.setAsideVisible(false);
        this.results.innerHTML = flagBlock(flag, this.texts);
        if (flag.handle) this.appendFlagProduct(flag);
        return;
      }

      this.suggestTimer = setTimeout(() => this.showSuggestions(value), SUGGEST_DEBOUNCE_MS);

      // Und wer aufhoert zu tippen, hat gefragt.
      if (value.length >= MIN_AUTO_SEARCH_LENGTH) {
        this.searchTimer = setTimeout(() => this.run(value), SEARCH_DEBOUNCE_MS);
      }
    };

    /* ---------- Auf und zu ---------- */

    open(query = '') {
      if (this.hasAttribute('open')) return;

      this.previousFocus = document.activeElement;
      this.pageScrollY = window.scrollY;
      this.positionBelowAnnouncement();
      this.setAttribute('open', '');
      document.body.classList.add('ai-search-open');

      if (query) {
        this.input.value = query;
        this.classList.add('ai-search--filled');
        this.run(query);
      } else {
        this.showIdle();
      }

      // Auf dem Telefon holt focus() die Tastatur hoch, und genau dafuer ist
      // die Suche da: es soll getippt werden.
      this.input?.focus();
    }

    /**
     * Die Ankuendigungsleiste bleibt sichtbar, das Overlay beginnt darunter.
     * Gemessen wird beim Oeffnen, nicht in CSS festgeschrieben: die Leiste ist
     * abschaltbar, mehrzeilig auf schmalen Schirmen und beim Scrollen halb weg.
     */
    positionBelowAnnouncement() {
      const bar = document.querySelector('[id*="announcement-bar"]');
      const rect = bar?.getBoundingClientRect();
      // Nur eine ganz sichtbare Leiste bleibt stehen. Ist die Seite ein Stueck
      // gescrollt, deckt das Overlay sie ganz ab, statt sie mitten im Text
      // abzuschneiden. Abgerundet, nicht gerundet: ein halber Pixel zu tief ist
      // ein sichtbarer heller Strich zwischen Leiste und Ankuendigung.
      const bottom = rect && rect.top >= 0 ? Math.max(0, Math.floor(rect.bottom)) : 0;
      this.style.setProperty('--ai-search-top', `${bottom}px`);
    }

    close() {
      if (!this.hasAttribute('open')) return;

      this.removeAttribute('open');
      document.body.classList.remove('ai-search-open');
      this.engine.abort();
      clearTimeout(this.suggestTimer);
      clearTimeout(this.searchTimer);
      this.previousFocus?.focus?.();
    }

    /* ---------- Zustaende ---------- */

    showIdle() {
      this.clearLoading();
      this.lastQuery = '';
      this.results.innerHTML = this.idleMarkup;
      this.setAsideVisible(true);
    }

    /**
     * Die Sidebar (beliebte Tests, Hinweis) gehoert zum Einstieg. Sobald eine
     * Antwort dasteht, ist sie Ablenkung - dann traegt die Antwort selbst,
     * was noch dazugehoert.
     */
    setAsideVisible(visible) {
      if (this.aside) this.aside.hidden = !visible;
    }

    async showSuggestions(query) {
      let results;
      try {
        results = await this.engine.suggest(query);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        // Keine Vorschlaege ist kein Zustand, den jemand sehen muss: das Feld
        // funktioniert weiter, Enter fragt den Worker.
        return;
      }

      if (this.input.value.trim() !== query) return;

      const queries = (results.queries || []).slice(0, 3);
      /* "Produkte dazu" ist Shopifys Wortsuche auf die ersten Buchstaben, vor
         der Antwort. Auf dem Telefon fuellte sie den Platz direkt ueber der
         Tastatur mit Beinahe-Treffern, also bleibt sie am Desktop. */
      const products = window.matchMedia('(max-width: 899px)').matches
        ? []
        : (results.products || []).slice(0, 3);

      /*
       * Shopifys Wortsuche kennt den Satz nicht, den jemand gerade tippt - das
       * ist der Normalfall bei einer Beschwerde und kein Zustand, den man
       * zeigen muss. Der Einstieg bleibt stehen, bis Enter kommt.
       */
      if (!queries.length && !products.length) return;

      this.results.innerHTML = `
        ${
          queries.length
            ? `<div class="ai-search__panel-label ai-search__label">${escapeHtml(
                this.texts.suggestLabel || ''
              )}</div>
              <div class="ai-search__suggestions">
                ${queries
                  .map(
                    (entry) => `
                      <button type="button" class="ai-search__suggestion" data-ai-query="${escapeHtml(
                        entry.text
                      )}">
                        ${ICON.search}<span>${entry.styled_text || escapeHtml(entry.text)}</span>
                      </button>`
                  )
                  .join('')}
              </div>`
            : ''
        }
        ${
          products.length
            ? `<div class="ai-search__label" style="margin-top:6px">${escapeHtml(
                this.texts.productsLabel || ''
              )}</div>
              <div class="ai-search__suggestions">
                ${products
                  .map((product) =>
                    suggestionRow({
                      handle: product.handle,
                      title: product.title,
                      image: product.featured_image?.url || product.image,
                      price: product.price
                        ? {
                            amount: Number(String(product.price).replace(/[^\d.]/g, '')),
                            currencyCode: window.Shopify?.currency?.active || 'EUR',
                          }
                        : null,
                    })
                  )
                  .join('')}
              </div>`
            : ''
        }
        ${this.enterHint()}
      `;
      this.setAsideVisible(false);
    }

    enterHint() {
      if (!this.texts.enterHint) return '';
      return `<div class="ai-search__enter-hint"><span>Enter</span><span>${escapeHtml(
        this.texts.enterHint
      )}</span></div>`;
    }

    /**
     * Enter: erst die Warnliste, dann die Rueckfrage, dann der Worker. Die
     * Reihenfolge ist die Rangfolge - eine Warnung darf nie hinter einer
     * Produktliste landen.
     */
    async run(query) {
      clearTimeout(this.suggestTimer);
      clearTimeout(this.searchTimer);
      this.lastQuery = query;
      this.setAsideVisible(false);

      /* Eine neue Frage beginnt oben. Wer weit unten auf eine Kachel oder
         einen Vorschlag tippte, blieb sonst unten stehen und sah zuerst das
         Ende der Antwort. */
      this.topFor = query;
      this.scrollListToTop();

      const flag = matchesFlag(query, this.flags);
      if (flag) {
        this.results.innerHTML = flagBlock(flag, this.texts);
        // Auch im Warnfall darf stehen, was tatsaechlich passt - nur eben
        // darunter, klein, und ohne Warenkorb-Knopf davor.
        if (flag.handle) await this.appendFlagProduct(flag);
        return;
      }

      const clarifier = matchesClarifier(query, this.clarifiers);
      if (clarifier) return this.showClarifier(clarifier);

      /*
       * Bevor irgendetwas ueber die Leitung geht, zeigt das Theme, was es
       * selbst fuer passend haelt. Das ist nicht die Antwort - sie steht als
       * "wird geprueft" dran und wird gleich ersetzt oder umsortiert -, aber es
       * ist echter Inhalt an der Stelle, an der sonst anderthalb Sekunden lang
       * graue Balken standen.
       */
      const guessed = this.showGuess(query);
      if (!guessed) this.showLoading();

      let data;
      try {
        data = await this.engine.ask(query, (partial) => {
          // Die Karten stehen, sobald das Modell sie geschrieben hat. Der Satz
          // darueber bleibt so lange ein Umriss - er kommt eine halbe Sekunde
          // spaeter, und bis dahin hat der Shopper schon etwas zu lesen.
          if (this.lastQuery !== query || !partial.handles.length) return;
          this.data = partial;
          this.activeFacet = null;
          this.renderAnswer(query, partial, true);
        });
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.warn('ai-search: Worker nicht erreichbar', error);
        return this.showFallback(query);
      }

      if (this.lastQuery !== query) return;
      this.data = data;
      this.activeFacet = null;
      this.renderAnswer(query, data);
    }

    /*
     * Das Modell schreibt ein paar Sekunden. Ein Spinner mit "wir suchen" macht
     * daraus eine gefühlte Ewigkeit; die Umrisse dessen, was gleich kommt,
     * machen daraus ein Laden. Deshalb steht hier die Antwort schon als Form
     * da, nur ohne Inhalt.
     */
    /**
     * Die eigene Vermutung als Karten. Gibt zurueck, ob etwas dastand - wenn
     * nicht, uebernehmen die Umrisse wie bisher.
     */
    showGuess(query) {
      const guesses = guessProducts(query, this.catalog);
      if (!guesses.length) return false;

      this.clearLoading();
      const note = this.texts.guessNote || 'wird geprüft';
      const results = guesses.map((product) => ({ ...product, note, reasons: [] }));
      this.renderAnswer(query, { results, products: results }, true);
      return true;
    }

    showLoading() {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => this.paintLoading(), LOADING_DELAY_MS);
    }

    /* Nimmt die Umrisse wieder weg, sobald echter Inhalt kommt - auch dann,
       wenn er schneller da war, als sie erscheinen wollten. */
    clearLoading() {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }

    paintLoading() {
      this.results.innerHTML = `
        ${answerBones(this.texts.loading || '')}
        <div class="ai-search__results">
          ${['', '', '']
            .map(
              () => `
                <div class="ai-search__skeleton">
                  <span class="ai-search__bone ai-search__bone--thumb"></span>
                  <span class="ai-search__bone ai-search__bone--title"></span>
                  <span class="ai-search__bone ai-search__bone--price"></span>
                </div>`
            )
            .join('')}
        </div>
      `;
    }

    showClarifier(clarifier) {
      this.clearLoading();
      const options = (clarifier.options || [])
        .map(
          (option) => `
            <button type="button" class="ai-search__starter" data-ai-query="${escapeHtml(
              option.query
            )}">
              <span class="ai-search__starter-text">
                <span class="ai-search__starter-title">${escapeHtml(option.title)}</span>
                ${
                  option.note
                    ? `<span class="ai-search__starter-note">${escapeHtml(option.note)}</span>`
                    : ''
                }
              </span>
            </button>`
        )
        .join('');

      this.results.innerHTML = `
        ${answerBlock({
          label: this.texts.clarifyLabel || '',
          heading: clarifier.heading || '',
          text: clarifier.text || '',
          chips: [],
        })}
        <div class="ai-search__starters">${options}</div>
      `;
    }

    renderAnswer(query, data, pending) {
      this.clearLoading();
      const products = data.products || [];
      const results = data.results || [];

      // Der Worker schickt Treffer und Begruendungen getrennt; die Karte
      // braucht beides an einem Stueck.
      const byHandle = new Map(products.map((product) => [product.handle, product]));
      const merged = results.map((result) => ({
        ...result,
        reasons: byHandle.get(result.handle)?.reasons || [],
        groups: byHandle.get(result.handle)?.groups || [],
      }));

      if (!merged.length) return this.showEmpty(query, data);

      /*
       * Der Worker schickt die Antwort als Ueberschrift, Fliesstext und ein
       * paar Schlagworte. Fehlt sie (Wortsuche, oder das Modell hat nichts
       * Brauchbares geschrieben), steht die Ueberschrift aus den Einstellungen
       * da und die Karten tragen die Antwort allein.
       */
      const answer = data.answer && typeof data.answer === 'object' ? data.answer : null;
      const heading = answer?.headline || this.texts.resultsHeading || '';
      const body = answer?.body || '';
      /* Kein "Wir haben gesucht nach …" mehr hinter dem Text: die
         Tippfehler-Korrektur des Workers lag oft daneben ("anfangen" wurde
         "anfaenger"). Sie laeuft weiter, sie wird nur nicht mehr angezeigt. */

      this.results.innerHTML = `
        ${
          pending
            ? answerBones(this.texts.loading || '')
            : heading
              ? answerBlock({
                  label: this.texts.answerLabel || '',
                  heading,
                  text: body,
                  chips: answer?.tags || [],
                })
              : ''
        }
        <ul class="ai-search__results" data-ai-list>
          ${merged.map((product) => resultCard(product, this.texts)).join('')}
        </ul>
        ${
          data.total > merged.length
            ? `<a class="ai-search__status" href="${rootUrl()}search?q=${encodeURIComponent(
                query
              )}">${escapeHtml(this.texts.allResults || '')}</a>`
            : ''
        }
        ${
          (data.related || []).length
            ? `<div class="ai-search__panel">
                <span class="ai-search__label">${escapeHtml(this.texts.relatedLabel || '')}</span>
                <div class="ai-search__chips">
                  ${data.related
                    .map(
                      (entry) =>
                        `<button type="button" class="ai-search__chip" data-ai-query="${escapeHtml(
                          entry
                        )}">${escapeHtml(entry)}</button>`
                    )
                    .join('')}
                </div>
              </div>`
            : ''
        }
        ${disclaimerBlock(this.texts.disclaimer)}
      `;

      this.merged = merged;

      // Noch einmal nach oben, sobald die ersten Karten fuer die neue Frage
      // stehen: auf dem iPhone kam der erste Versuch beim Start nicht immer an.
      if (this.topFor === query) {
        this.topFor = null;
        requestAnimationFrame(() => this.scrollListToTop());
      }
    }

    /*
     * Zurueck an den Anfang der Liste, auf dem umstaendlichen Weg, weil Safari
     * auf dem iPhone scrollTo() und ein schlichtes scrollTop = 0 ignorierte:
     * - eine Liste, die nach einem Wisch noch nachlaeuft, ueberschreibt jede
     *   gesetzte Position. Overflow einen Frame aus stoppt das Nachlaufen.
     * - mit offener Tastatur verschiebt das iPhone manchmal die Seite statt
     *   der Liste. Die geht zurueck auf die Position beim Oeffnen der Suche.
     */
    scrollListToTop() {
      const list = this.querySelector('.ai-search__body');
      if (list) {
        list.style.overflowY = 'hidden';
        list.scrollTop = 0;
        requestAnimationFrame(() => {
          list.scrollTop = 0;
          list.style.overflowY = '';
        });
      }

      this.scrollTop = 0;
      if (typeof this.pageScrollY === 'number' && window.scrollY !== this.pageScrollY) {
        window.scrollTo(0, this.pageScrollY);
      }
    }

    showEmpty(query, data) {
      this.results.innerHTML = `
        <div class="ai-search__answer" style="background:#ffffff;border-color:#e4dfd4">
          <div class="ai-search__label">${escapeHtml(this.texts.emptyLabel || '')}</div>
          <h2 class="ai-search__empty-heading">${escapeHtml(this.texts.emptyHeading || '')}</h2>
          <p class="ai-search__answer-text">${escapeHtml(this.texts.emptyText || '')}</p>
        </div>
        ${this.categoriesMarkup()}
      `;
      this.setAsideVisible(false);
      void data;
      void query;
    }

    /**
     * Faellt der Worker aus, bleibt die Suche des Shops. Sie versteht keine
     * Saetze, findet aber Produktnamen - besser als eine Fehlermeldung.
     */
    showFallback(query) {
      this.clearLoading();
      this.results.innerHTML = `
        <div class="ai-search__status">${escapeHtml(this.texts.errorText || '')}</div>
        <a class="ai-search__card-button button-size" style="max-width:280px" href="${rootUrl()}search?q=${encodeURIComponent(
          query
        )}">${escapeHtml(this.texts.errorButton || '')}</a>
      `;
    }

    categoriesMarkup() {
      const template = this.querySelector('[data-ai-categories]');
      return template ? template.innerHTML : '';
    }

    async appendFlagProduct(flag) {
      const url = `${rootUrl()}products/${encodeURIComponent(flag.handle)}.js`;
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const product = await response.json();

        this.results.insertAdjacentHTML(
          'beforeend',
          `
            <div class="ai-search__label" style="margin-top:6px">${escapeHtml(
              this.texts.flagProductLabel || ''
            )}</div>
            <ul class="ai-search__results">
              ${resultCard(
                {
                  handle: product.handle,
                  title: product.title,
                  image: product.featured_image,
                  price: {
                    amount: product.price / 100,
                    currencyCode: window.Shopify?.currency?.active || 'EUR',
                  },
                  reasons: [],
                  note: flag.note || '',
                },
                { ...this.texts, cardButton: this.texts.flagProductButton || this.texts.cardButton }
              )}
            </ul>
          `
        );
      } catch (error) {
        // Ohne das Produkt steht die Warnung allein da, und das ist in
        // Ordnung - sie ist die Antwort, nicht das Produkt.
      }
    }

    /* ---------- Warenkorb ---------- */

    async addFromCard(event, button) {
      const card = button.closest('[data-handle]');
      if (!card) return;

      const handle = card.dataset.handle;
      const label = button.textContent;

      event.preventDefault();
      button.setAttribute('disabled', '');
      button.textContent = this.texts.cardButtonLoading || label;

      try {
        const variantId = await resolveVariant(handle);
        // Mehrere Varianten oder ausverkauft: dann fuehrt der Knopf auf die
        // Produktseite, wo beides zu sehen ist.
        if (!variantId) {
          window.location.href = button.getAttribute('href');
          return;
        }

        await addToCart(variantId);
        button.textContent = this.texts.cardButtonDone || label;
        this.close();
      } catch (error) {
        console.warn('ai-search: In den Warenkorb fehlgeschlagen', error);
        window.location.href = button.getAttribute('href');
      } finally {
        button.removeAttribute('disabled');
        setTimeout(() => {
          button.textContent = label;
        }, 2000);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Die Suchergebnisseite.
   *
   * Dort hat Shopify schon gesucht. Der Worker kommt dazu, wenn die Wortsuche
   * wenig oder nichts gefunden hat - und der Antwortsatz steht dann ueber dem,
   * was die Seite ohnehin zeigt.
   * ------------------------------------------------------------------ */

  class AiSearchPage extends HTMLElement {
    connectedCallback() {
      this.engine = new SearchEngine(this);
      this.texts = config().texts || {};
      this.flags = config().flags || [];
      this.known = (this.dataset.known || '').split(',').filter(Boolean);

      this.addEventListener('click', (event) => {
        const add = event.target.closest('[data-ai-add]');
        if (add) AiSearchOverlay.prototype.addFromCard.call(this, event, add);
      });

      this.run();
    }

    disconnectedCallback() {
      this.engine?.abort();
    }

    async run() {
      const query = this.dataset.query;
      if (!query) return;

      const flag = matchesFlag(query, this.flags);
      if (flag) {
        this.innerHTML = flagBlock(flag, this.texts);
        return;
      }

      this.innerHTML = `
        <div class="ai-search__status">
          <span class="ai-search__spinner" aria-hidden="true"></span>
          <span>${escapeHtml(this.texts.loading || '')}</span>
        </div>
      `;

      let data;
      try {
        data = await this.engine.ask(query);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        // Auf der Seite steht schon eine Antwort - hier verschwindet der Block
        // einfach, statt eine Stoerung zu melden.
        this.remove();
        return;
      }

      const products = data.products || [];
      const byHandle = new Map(products.map((product) => [product.handle, product]));
      const merged = (data.results || [])
        .filter((result) => !this.known.includes(result.handle))
        .map((result) => ({
          ...result,
          reasons: byHandle.get(result.handle)?.reasons || [],
        }));

      if (!merged.length && !data.answer?.headline) {
        this.remove();
        return;
      }

      this.innerHTML = `
        ${
          data.answer?.headline
            ? answerBlock({
                label: this.texts.answerLabel || '',
                heading: data.answer.headline,
                text: data.answer.body || '',
                chips: data.answer.tags || [],
              })
            : ''
        }
        ${
          merged.length
            ? `<div class="ai-search__label">${escapeHtml(
                this.known.length ? this.texts.moreHeading || '' : this.texts.resultsLabel || ''
              )}</div>
              <ul class="ai-search__results">
                ${merged.map((product) => resultCard(product, this.texts)).join('')}
              </ul>`
            : ''
        }
        ${disclaimerBlock(this.texts.disclaimer)}
      `;

      // Der Satz "nichts gefunden" der Seite gilt nicht mehr, sobald hier
      // Karten stehen.
      if (merged.length) document.querySelector('[data-search-no-results]')?.remove();
    }
  }

  if (!customElements.get('ai-search-overlay')) {
    customElements.define('ai-search-overlay', AiSearchOverlay);
  }
  if (!customElements.get('ai-search-page')) {
    customElements.define('ai-search-page', AiSearchPage);
  }
})();
